import { apiClient } from '@/http/apiClient';
import { buildQuery, joinUrl } from '@/http/url';
import { AxiosResponse } from 'axios';
const rootPath = 'Export';

export type ExportDto = {
    name:string,
    generatedExcelData: string,
}

class ExportService {
    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = window.atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    private downloadBlob(blob: Blob, name: string): void {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = name;
        document.body.appendChild(link);
        link.dispatchEvent(
            new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            })
        );
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    }

    private getFileNameFromResponse(response: AxiosResponse<Blob>, fallbackName: string): string {
        const contentDisposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
        if (!contentDisposition) {
            return fallbackName;
        }

        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match?.[1]) {
            return decodeURIComponent(utf8Match[1]);
        }

        const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        if (asciiMatch?.[1]) {
            return asciiMatch[1];
        }

        return fallbackName;
    }

    public async get() {
        return await apiClient.get(`${rootPath}`);
    }

    public getUrl(): string {
        return joinUrl(apiClient.defaults.baseURL || '', rootPath);
    }

    public exportBopModelUrl(bopModelKey:string,language:string):string{
        return `${this.getUrl()}/bopModels/${bopModelKey}${buildQuery({language})}`;
    }
    public exportBopModel(bopModelKey:string,language:string): Promise<AxiosResponse<ExportDto>>{
        return apiClient.get(this.exportBopModelUrl(bopModelKey,language));
    }

    public exportBopsUrl(adminExportId: string): string {
        return `${this.getUrl()}/bops/${adminExportId}`;
    }

    public exportBops(adminExportId: string): Promise<AxiosResponse<ExportDto>> {
        return apiClient.get(this.exportBopsUrl(adminExportId));
    }

    public downloadExport(exportDto: ExportDto): void {
        const data = this.base64ToArrayBuffer(exportDto.generatedExcelData);
        const blob = new Blob([data], { type: 'application/octet-stream' });
        this.downloadBlob(blob, exportDto.name);
    }

    public async downloadBops(adminExportId: string, fallbackName: string): Promise<void> {
        const response = await this.exportBops(adminExportId);
        this.downloadExport(response.data);
    }
}

export default new ExportService();